import { events } from 'aws-amplify/data';
import { updateEvents } from './slices/applicant-detail-slices/documents-slice';
import { updateExportEvents } from './slices/exports-slice';
import { addJob, receiveJobEvent } from './slices/jobs-slice';
import { graphqlApi } from './api';

const jobEvent = (key) => (data) => receiveJobEvent({ key, event: typeof data.event === 'string' ? JSON.parse(data.event) : data.event });

const documentFetchHandler = (getState) => (data) => (dispatch) => {
    let event = data?.event;
    if (typeof event === 'string') {
        try { event = JSON.parse(event); } catch { return; }
    }
    const receiptId = event?.receiptId;
    if (!receiptId) return;
    dispatch(updateEvents({ event }));
    const uuid = getState().documentsSlice?.pendingDownloadsByReceiptId?.[receiptId]?.uuid;
    if (uuid) dispatch(graphqlApi.endpoints.getDocumentStatus.initiate({ uuid }, { forceRefetch: true }));
};

// Scholars "Initiate Preferences" run completion. Resolve the navbar job badge (done/failed) AND refresh
// the Not Preferenced report so processed applicants drop off without a manual reload.
const scholarsPreferencingHandler = () => (data) => (dispatch) => {
    let event = data?.event;
    if (typeof event === 'string') {
        try { event = JSON.parse(event); } catch { return; }
    }
    if (!event?.executionId) return;
    dispatch(receiveJobEvent({ key: 'scholarsPreferencing', event }));
    dispatch(graphqlApi.util.invalidateTags(['getReport']));
};

// Scholars "Publish" run completion (has575). Resolve the navbar job badge (done/failed) AND refresh the
// Publish/Completed reports so published applicants drop off Publish and appear on Completed without a
// manual reload. Mirrors scholarsPreferencingHandler.
const scholarsPublishHandler = () => (data) => (dispatch) => {
    let event = data?.event;
    if (typeof event === 'string') {
        try { event = JSON.parse(event); } catch { return; }
    }
    if (!event?.executionId) return;
    dispatch(receiveJobEvent({ key: 'scholarsPublish', event }));
    dispatch(graphqlApi.util.invalidateTags(['getReport']));
};

// Scholars Review Cascade realtime. The backend publishes ScholarsCascadeChanged on ANY cascade/queue
// state change (pull, recommend, decision, enqueue/skip, exhaust, advance) — including cross-user actions
// and async cascade-engine advances. On any event, invalidate the cascade page's data AND the Common Queue
// cards so they refetch live — no manual reload:
//   - ScholarsQueue     → cascade overview sidebar counts + Common Queue "In Progress" grid + program-queue grids
//   - ScholarsPrograms  → Common Queue program CARDS (New / In-Progress / Past-Due / Admitted tallies). These read
//                         getScholarsPrograms; a DENY places the next-preference QUEUED entry OUT OF BAND, so the
//                         card counts would otherwise stay stale until a manual refresh even though the event arrived.
//   - getReport         → open program grid on the cascade page
const scholarsCascadeHandler = () => () => (dispatch) => {
    dispatch(graphqlApi.util.invalidateTags(['ScholarsQueue', 'ScholarsPrograms', 'getReport']));
};

// has572 — Advanced Search bulk admin recommendation (deny + admit) run completion. Resolve the navbar job
// badge (done/failed) AND refresh the reports the run affects so processed applicants move without a manual
// reload: the Opt-In List (ScholarsAlternateList — admitted/denied opt-in members drop off) and the report
// grid (getReport — e.g. applicants now appear on Recommendations to Approve). Mirrors scholarsPublishHandler.
const scholarsBulkAdminRecHandler = () => (data) => (dispatch) => {
    let event = data?.event;
    if (typeof event === 'string') {
        try { event = JSON.parse(event); } catch { return; }
    }
    if (!event?.executionId) return;
    dispatch(receiveJobEvent({ key: 'scholarsBulkAdminRec', event }));
    dispatch(graphqlApi.util.invalidateTags(['ScholarsAlternateList', 'getReport', 'ScholarsQueue', 'ScholarsPrograms']));
};

const CHANNEL_CONFIGS = [
    { path: '/documents/fetch', handlerFactory: documentFetchHandler },
    { path: '/documents/export', action: updateExportEvents },
    { path: '/reports/recommendations-approved', action: jobEvent('recommendations') },
    { path: '/search/change-workflow-complete', action: jobEvent('changeWorkflow') },
    { path: '/search/override-workflow-decision-complete', action: jobEvent('overrideWorkflowDecision') },
    { path: '/search/scholars-bulk-admin-rec-complete', handlerFactory: scholarsBulkAdminRecHandler },
    { path: '/reports/publish-complete', action: jobEvent('publish') },
    { path: '/reports/program-change-or-withdraw-complete', action: jobEvent('programChangeOrWithdrawal') },
    { path: '/reports/scholars-preferencing-complete', handlerFactory: scholarsPreferencingHandler },
    { path: '/reports/scholars-publish-complete', handlerFactory: scholarsPublishHandler },
    { path: '/scholars/cascade', handlerFactory: scholarsCascadeHandler },
];

const JOB_LABELS = {
    '/reports/recommendations-approved': () => 'Submitting approvals...',
    '/reports/publish-complete': () => 'Publishing applications...',
    '/reports/scholars-preferencing-complete': () => 'Initiating preferences...',
    '/reports/scholars-publish-complete': () => 'Publishing to PeopleSoft...',
    '/reports/program-change-or-withdraw-complete': () => 'Processing program change or withdraw requests...',
    '/search/change-workflow-complete': () => 'Processing workflow changes...',
    '/search/override-workflow-decision-complete': () => 'Processing administrative overrides...',
    '/search/scholars-bulk-admin-rec-complete': () => 'Processing bulk admin recommendation...',
};

const channelState = {};

export async function connectEventChannel(path, action, dispatch, getState) {
    if (process.env.REACT_APP_API_EVENTS_DISABLED === 'true') return;
    if (channelState[path]?.connected) return;
    if (channelState[path]?.connecting) return channelState[path].promise;

    let resolve;
    const promise = new Promise(r => { resolve = r; });
    channelState[path] = { connecting: true, connected: false, promise };
    try {
        console.debug(`[event-channel] connecting to ${path}...`);
        const channel = await events.connect(path);
        channelState[path] = { connecting: false, connected: true, channel };
        console.debug(`[event-channel] connected to ${path}`);
        channel.subscribe({
            next: (data) => {
                console.debug(`[event-channel] received on ${path}`, data);
                const result = action(data);
                if (typeof result === 'function') result(dispatch);
                else dispatch(result);
            },
            error: (err) => {
                console.error(`[event-channel] error on ${path}`, err);
                channelState[path] = { connecting: false, connected: false };
                setTimeout(() => connectEventChannel(path, action, dispatch, getState), 3000);
            },
        });
    } catch (err) {
        console.error(`[event-channel] failed to connect to ${path}`, err);
        channelState[path] = { connecting: false, connected: false };
        setTimeout(() => connectEventChannel(path, action, dispatch, getState), 3000);
    }
    resolve();
}



export const getOnQueryStarted = (reportPath) => {
    return async (variables, { dispatch, queryFulfilled }) => {
        await connectChannel(reportPath, dispatch);
        try {
            console.debug("reportPath in onQueryStarted:", reportPath);
            const label = JOB_LABELS[reportPath]?.(variables) ?? reportPath;
            const { data } = await queryFulfilled;
            dispatch(addJob({ id: data?.executionId, label }));
        } catch { 
            console.error(`Error starting job for ${reportPath}`); 
        }
    }
}

export const connectChannel = async (path, dispatch, getState) => {
    const config = CHANNEL_CONFIGS.find(c => c.path === path);
    if (!config) { console.warn(`[event-channel] no config found for path: ${path}`); return; }
    const action = config.handlerFactory ? config.handlerFactory(getState) : config.action;
    await connectEventChannel(config.path, action, dispatch, getState);
};

